import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, Download, ExternalLink, Eye, FileCheck2, FileSignature, ScrollText, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePatchRequest, useGetRequest } from '@/shared/hooks/requests';
import {
  WORKFLOW,
  WORKFLOW_RENDITION,
  WORKFLOW_QR,
  WORKFLOW_PKCS7,
  WORKFLOW_SIGNATURE_SHEET,
  WORKFLOW_VERIFICATION_VIEWS,
} from '@/shared/utils/urls';
import { downloadBlob, fetchAuthedBlob, openBlob, useAuthedObjectUrl } from '@/shared/utils/authedFetch';

const STATUS_TONE = {
  signed: 'text-green-600 dark:text-green-400 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20',
  partially_signed: 'text-amber-600 dark:text-amber-400 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20',
  pending: 'text-muted-foreground border-border bg-muted/40',
  rejected: 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
  withdrawn: 'text-muted-foreground border-border bg-muted/40',
};

/**
 * The verification side of a signed document: the code stamped into the
 * rendition, its QR, the public link, and the artefacts beside it (the
 * stamped PDF the signers sign, the PKCS#7 container, the signature
 * sheet). The initiator also sets who may open the link.
 *
 * `collection` is the camelized workflow read (axios); `guestToken`
 * overrides the auth header for guest signers.
 */
export const VerificationBlock = ({ collection, fileId, isInitiator, guestToken, onChanged }) => {
  const { t, i18n } = useTranslation();
  const code = collection?.verificationCode;
  const url = collection?.verificationUrl;
  const signingStatus = collection?.signingStatus || 'pending';
  const hasSignatures = (collection?.participants || []).some((p) => p.status === 'signed');
  const headers = guestToken
    ? { Authorization: `Bearer ${guestToken}`, 'accept-language': i18n.language }
    : { 'accept-language': i18n.language };
  const qrUrl = useAuthedObjectUrl(code ? WORKFLOW_QR.replace('{file_id}', fileId) : null, { headers });
  const [busy, setBusy] = useState(null);

  const patchWorkflow = usePatchRequest({ url: WORKFLOW.replace('{file_id}', fileId) });
  const views = useGetRequest({ url: WORKFLOW_VERIFICATION_VIEWS.replace('{file_id}', fileId), showError: false });
  useEffect(() => {
    if (isInitiator && code) views.request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitiator, code]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('signing:verification.linkCopied'));
    } catch {
      toast.error(t('signing:verification.copyFailed'));
    }
  }, [url, t]);

  const fetchArtefact = useCallback(
    async (kind) => {
      const path = {
        pdf: WORKFLOW_RENDITION,
        pkcs7: WORKFLOW_PKCS7,
        sheet: `${WORKFLOW_SIGNATURE_SHEET}?lang=${encodeURIComponent(i18n.language)}`,
      }[kind].replace('{file_id}', fileId);
      setBusy(kind);
      try {
        const blob = await fetchAuthedBlob(path, { headers });
        const stem = (collection?.fileName || `document-${fileId}`).replace(/\.[^.]+$/, '');
        if (kind === 'pdf') openBlob(blob);
        else if (kind === 'sheet') openBlob(blob);
        else downloadBlob(blob, `${stem}.signatures.${blob.type === 'application/zip' ? 'zip' : 'p7m'}`);
      } catch (err) {
        toast.error(err.detail?.message || t('signing:verification.downloadFailed'));
      } finally {
        setBusy(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileId, i18n.language, guestToken, collection?.fileName, t],
  );

  const setVisibility = async (visibility) => {
    const res = await patchWorkflow.request({ data: { visibility } });
    if (res.success) {
      toast.success(t('signing:verification.visibilitySaved'));
      if (onChanged) onChanged();
    }
  };

  if (!code) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" />
          {t('signing:verification.title')}
        </span>
        <Badge variant="outline" className={`h-5 text-[10px] ${STATUS_TONE[signingStatus] || STATUS_TONE.pending}`}>
          {t(`signing:verification.status.${signingStatus}`)}
        </Badge>
      </div>

      <div className="flex gap-3 items-start p-2.5 rounded-md border border-border bg-muted/30">
        <div className="w-[76px] h-[76px] shrink-0 rounded bg-white p-1 border border-border flex items-center justify-center">
          {qrUrl ? (
            <img src={qrUrl} alt={t('signing:verification.qrAlt')} className="w-full h-full" />
          ) : (
            <div className="w-full h-full animate-pulse bg-muted rounded" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('signing:verification.code')}</div>
          <div className="font-mono text-sm font-semibold tracking-wider text-foreground break-all">{code}</div>
          <div className="text-[11px] text-muted-foreground truncate" title={url}>{url}</div>
          <div className="flex flex-wrap gap-1 pt-1">
            <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={copyLink}>
              <Copy className="w-3.5 h-3.5 mr-1" />
              {t('signing:verification.copyLink')}
            </Button>
            <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs">
              <a href={url} target="_blank" rel="noreferrer">
                <ExternalLink className="w-3.5 h-3.5 mr-1" />
                {t('signing:verification.open')}
              </a>
            </Button>
          </div>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground leading-snug">
        {collection?.qrPlacement === 'marker'
          ? t('signing:verification.stampedMarker', { marker: '{{qr}}' })
          : t('signing:verification.stampedBox')}
      </p>

      <div className="grid grid-cols-1 gap-1.5">
        <Button variant="outline" size="sm" className="justify-start h-8 text-xs" disabled={busy === 'pdf'} onClick={() => fetchArtefact('pdf')}>
          <FileCheck2 className="w-3.5 h-3.5 mr-2 text-brand" />
          {t('signing:verification.openRendition')}
        </Button>
        {hasSignatures && (
          <Button variant="outline" size="sm" className="justify-start h-8 text-xs" disabled={busy === 'pkcs7'} onClick={() => fetchArtefact('pkcs7')}>
            <FileSignature className="w-3.5 h-3.5 mr-2 text-brand" />
            {t('signing:verification.downloadPkcs7')}
            <Download className="w-3 h-3 ml-auto opacity-60" />
          </Button>
        )}
        <Button variant="outline" size="sm" className="justify-start h-8 text-xs" disabled={busy === 'sheet'} onClick={() => fetchArtefact('sheet')}>
          <ScrollText className="w-3.5 h-3.5 mr-2 text-brand" />
          {t('signing:verification.openSheet')}
        </Button>
      </div>

      {isInitiator && (
        <div className="space-y-1.5 pt-1">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t('signing:verification.visibility')}</div>
          <Select value={collection?.visibility || 'restricted'} onValueChange={setVisibility}>
            <SelectTrigger className="w-full h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['restricted', 'public', 'off'].map((v) => (
                <SelectItem key={v} value={v}>
                  <div className="flex flex-col">
                    <span className="text-xs font-medium">{t(`signing:verification.visibilityOptions.${v}.label`)}</span>
                    <span className="text-[10px] text-muted-foreground">{t(`signing:verification.visibilityOptions.${v}.hint`)}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {views.response && (
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pt-0.5">
              <Eye className="w-3 h-3" />
              {views.response.total > 0
                ? t('signing:verification.viewed', {
                    count: views.response.total,
                    when: views.response.lastAt ? new Date(views.response.lastAt).toLocaleString(i18n.language) : '',
                    interpolation: { escapeValue: false },
                  })
                : t('signing:verification.neverViewed')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VerificationBlock;
